// File: LayeredPreview
// Created: 2026-05-29
// Updated: 2026-05-29
// Author: Erick Hernández Silva

import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { Overlay, Scenery, ViewKey } from './carWizardTypes';
import { isRenderableImage } from './carModel';

export interface Layer {
  url: string;
  type: string;
  zIndex: number;
}

interface Props {
  carName: string;
  selectedView: ViewKey;
  viewLoading: boolean;
  allViewLayers: Record<ViewKey, Layer[]>;
  imagesLoaded: Record<string, boolean>;
  setImagesLoaded: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  viewKeys: readonly ViewKey[];
  setSelectedView: (view: ViewKey) => void;
  setViewLoading: (loading: boolean) => void;
  stepLabel?: string;
  stepTitle?: string;
  embedded?: boolean;
  overlays?: Overlay[];
  activeOverlayIds?: string[];
  onToggleOverlay?: (overlayId: string) => void;
  sceneries?: Scenery[];
  sceneryId?: string | null;
  onSelectScenery?: (sceneryId: string | null) => void;
  onChangeModel?: () => void;
}

const LayeredPreview: React.FC<Props> = ({
  carName,
  selectedView,
  viewLoading,
  allViewLayers,
  imagesLoaded,
  setImagesLoaded,
  viewKeys,
  setSelectedView,
  setViewLoading,
  stepLabel,
  stepTitle,
  embedded = false,
  overlays = [],
  activeOverlayIds = [],
  onToggleOverlay,
  sceneries = [],
  sceneryId = null,
  onSelectScenery,
  onChangeModel,
}) => {
  const lg = (cls: string) => (embedded ? '' : cls);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const dragLock = useRef<null | 'h' | 'v'>(null);
  const dragFromRef = useRef<ViewKey>(selectedView);
  const containerWidthRef = useRef<number>(0);
  const skipAutoTransitionRef = useRef(false);

  const [dragX, setDragX] = useState(0);
  const [dragTo, setDragTo] = useState<ViewKey | null>(null);
  const [dragDir, setDragDir] = useState<-1 | 1>(1);
  const [isDragging, setIsDragging] = useState(false);
  const [isSettling, setIsSettling] = useState(false);

  const previousViewRef = useRef<ViewKey | null>(null);
  const [transition, setTransition] = useState<null | {
    from: ViewKey;
    to: ViewKey;
    dir: -1 | 1;
    stage: 'start' | 'end';
  }>(null);

  useEffect(() => {
    if (skipAutoTransitionRef.current) {
      skipAutoTransitionRef.current = false;
      previousViewRef.current = selectedView;
      return;
    }
    const prev = previousViewRef.current;
    if (prev && prev !== selectedView) {
      const prevIdx = viewKeys.findIndex(v => v === prev);
      const nextIdx = viewKeys.findIndex(v => v === selectedView);
      const dir: -1 | 1 = nextIdx >= prevIdx ? 1 : -1;
      setTransition({ from: prev, to: selectedView, dir, stage: 'start' });
      previousViewRef.current = selectedView;
      const raf = window.requestAnimationFrame(() => {
        setTransition(t => (t ? { ...t, stage: 'end' } : t));
      });
      const timeout = window.setTimeout(() => setTransition(null), 320);
      return () => {
        window.cancelAnimationFrame(raf);
        window.clearTimeout(timeout);
      };
    }
    previousViewRef.current = selectedView;
  }, [selectedView, viewKeys]);

  useEffect(() => {
    previousViewRef.current = selectedView;
  }, []);

  useEffect(() => {
    const update = () => {
      containerWidthRef.current = containerRef.current?.getBoundingClientRect().width || 0;
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const preloadUrls = useMemo(() => {
    const urls = new Set<string>();
    viewKeys.forEach((vk) => {
      (allViewLayers[vk] || []).forEach(l => {
        if (l.url) urls.add(l.url);
      });
    });
    return Array.from(urls);
  }, [allViewLayers, viewKeys]);

  const activeLayers = allViewLayers[selectedView] || [];

  // Overlay toggles only make sense in views that have a render for them.
  const viewOverlays = [...overlays]
    .filter((ov) => isRenderableImage(ov.images?.[selectedView]))
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const showSceneryPicker = sceneries.length > 0 && !!onSelectScenery;

  // Controls sit inside the swipe area: keep their taps from starting a drag.
  const stopDrag = {
    onPointerDown: (e: React.PointerEvent) => e.stopPropagation(),
    onTouchStart: (e: React.TouchEvent) => e.stopPropagation(),
  };

  const overlayButtons = (className: string) =>
    viewOverlays.map((ov) => {
      const on = activeOverlayIds.includes(ov.overlayId);
      return (
        <button
          key={ov.overlayId}
          type="button"
          aria-pressed={on}
          onClick={() => onToggleOverlay?.(ov.overlayId)}
          className={`${className} whitespace-nowrap transition-colors ${on ? 'text-black underline underline-offset-4' : 'text-gray-800 hover:text-black'}`}
        >
          {on ? ov.labelActive || ov.label : ov.label}
        </button>
      );
    });

  const sceneryButtons = (size: string) => {
    const hasDefaultStudio = !sceneries.some((s) => s.isDefault);
    const items: Array<{ id: string | null; name: string; thumb?: string }> = [
      ...(hasDefaultStudio ? [{ id: null, name: 'Studio' }] : []),
      ...sceneries.map((s) => ({ id: s.sceneryId, name: s.name, thumb: s.thumbnail })),
    ];
    return items.map((it) => {
      const on = (sceneryId ?? null) === it.id;
      return (
        <button
          key={it.id ?? 'studio'}
          type="button"
          title={it.name}
          aria-label={`Scenery: ${it.name}`}
          aria-pressed={on}
          onClick={() => onSelectScenery?.(it.id)}
          className={`${size} shrink-0 rounded-full overflow-hidden border bg-white transition-shadow ${on ? 'border-black ring-1 ring-black ring-offset-1' : 'border-gray-300 hover:border-gray-500'}`}
        >
          {isRenderableImage(it.thumb) && <img src={it.thumb} alt="" className="w-full h-full object-cover" />}
        </button>
      );
    });
  };

  useEffect(() => {
    preloadUrls.forEach((u) => {
      const img = new Image();
      img.src = u;
    });
  }, [preloadUrls]);

  const renderLayers = (layers: Layer[], viewKey: ViewKey) => {
    if (!layers || layers.length === 0) return null;
    // Stable key per layer (not by array index) so reordering steps only
    // updates z-index in place — no remount / image reload flash.
    return layers.map((layer) => (
      <img
        key={`${viewKey}-${layer.type}`}
        src={layer.url}
        alt={`${carName} - ${layer.type}`}
        className="absolute inset-0 w-full h-full object-contain transition-opacity duration-500"
        onLoad={() => {
          setImagesLoaded(prev => ({ ...prev, [`${layer.type}`]: true }));
        }}
        style={{
          opacity: imagesLoaded[`${layer.type}`] === false ? 0 : 1,
          zIndex: layer.zIndex,
        }}
      />
    ));
  };

  return (
    <div
      ref={containerRef}
      className={`relative bg-white overflow-hidden ${lg('lg:sticky lg:top-0 lg:h-screen lg:flex-1 lg:order-1')} order-1 flex flex-col ${embedded ? 'flex-1 min-h-0' : ''}`}
      style={{ touchAction: 'pan-y' }}
      onPointerDown={(e) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        touchStartX.current = e.clientX;
        touchStartY.current = e.clientY;
        dragLock.current = null;
        dragFromRef.current = selectedView;
        setIsDragging(false);
        setIsSettling(false);
        setDragX(0);
        setDragTo(null);
        (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
      }}
      onPointerMove={(e) => {
        const startX = touchStartX.current;
        const startY = touchStartY.current;
        if (typeof startX !== 'number' || typeof startY !== 'number') return;
        const moveX = e.clientX;
        const moveY = e.clientY;
        const dx = moveX - startX;
        const dy = moveY - startY;
        if (!dragLock.current) {
          if (Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy)) dragLock.current = 'h';
          else if (Math.abs(dy) > 8) dragLock.current = 'v';
        }
        if (dragLock.current !== 'h') return;
        const from = dragFromRef.current;
        const fromIdx = viewKeys.findIndex(v => v === from);
        if (fromIdx < 0) return;
        const nextDir: -1 | 1 = dx < 0 ? 1 : -1;
        const candidateIdx = fromIdx + nextDir;
        const candidate = candidateIdx >= 0 && candidateIdx < viewKeys.length ? viewKeys[candidateIdx] : null;
        setIsDragging(true);
        setDragDir(nextDir);
        setDragTo(candidate);
        const appliedDx = candidate ? dx : dx * 0.35;
        setDragX(appliedDx);
      }}
      onPointerUp={(e) => {
        const startX = touchStartX.current;
        touchStartX.current = null;
        touchStartY.current = null;
        const endX = e.clientX;
        if (dragLock.current !== 'h') {
          dragLock.current = null;
          return;
        }
        dragLock.current = null;
        if (typeof startX !== 'number') {
          setIsDragging(false);
          setIsSettling(false);
          setDragX(0);
          setDragTo(null);
          return;
        }
        const width = containerWidthRef.current || 1;
        const threshold = Math.max(50, width * 0.2);
        if (dragTo && Math.abs(endX - startX) > threshold) {
          setIsSettling(true);
          setDragX(endX - startX < 0 ? -width : width);
          window.setTimeout(() => {
            skipAutoTransitionRef.current = true;
            setSelectedView(dragTo);
            setViewLoading(false);
            setIsDragging(false);
            setIsSettling(false);
            setDragX(0);
            setDragTo(null);
          }, 260);
          return;
        }
        setIsSettling(true);
        setDragX(0);
        window.setTimeout(() => {
          setIsDragging(false);
          setIsSettling(false);
          setDragTo(null);
        }, 220);
      }}
      onTouchStart={(e) => {
        touchStartX.current = e.touches?.[0]?.clientX ?? null;
        touchStartY.current = e.touches?.[0]?.clientY ?? null;
        dragLock.current = null;
        dragFromRef.current = selectedView;
        setIsDragging(false);
        setIsSettling(false);
        setDragX(0);
        setDragTo(null);
      }}
      onTouchMove={(e) => {
        const startX = touchStartX.current;
        const startY = touchStartY.current;
        const moveX = e.touches?.[0]?.clientX;
        const moveY = e.touches?.[0]?.clientY;
        if (typeof startX !== 'number' || typeof startY !== 'number') return;
        if (typeof moveX !== 'number' || typeof moveY !== 'number') return;

        const dx = moveX - startX;
        const dy = moveY - startY;

        if (!dragLock.current) {
          if (Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy)) dragLock.current = 'h';
          else if (Math.abs(dy) > 8) dragLock.current = 'v';
        }

        if (dragLock.current !== 'h') return;

        const from = dragFromRef.current;
        const fromIdx = viewKeys.findIndex(v => v === from);
        if (fromIdx < 0) return;

        const nextDir: -1 | 1 = dx < 0 ? 1 : -1;
        const candidateIdx = fromIdx + nextDir;
        const candidate = candidateIdx >= 0 && candidateIdx < viewKeys.length ? viewKeys[candidateIdx] : null;

        setIsDragging(true);
        setDragDir(nextDir);
        setDragTo(candidate);

        const appliedDx = candidate ? dx : dx * 0.35;
        setDragX(appliedDx);
      }}
      onTouchEnd={(e) => {
        const startX = touchStartX.current;
        touchStartX.current = null;
        touchStartY.current = null;
        const endX = e.changedTouches?.[0]?.clientX;
        if (dragLock.current !== 'h') {
          dragLock.current = null;
          return;
        }
        dragLock.current = null;

        if (typeof startX !== 'number' || typeof endX !== 'number') {
          setIsDragging(false);
          setIsSettling(false);
          setDragX(0);
          setDragTo(null);
          return;
        }

        const width = containerWidthRef.current || 1;
        const threshold = Math.max(50, width * 0.2);

        if (dragTo && Math.abs(dragX) > threshold) {
          setIsSettling(true);
          setDragX(dragX < 0 ? -width : width);
          window.setTimeout(() => {
            skipAutoTransitionRef.current = true;
            setSelectedView(dragTo);
            setViewLoading(false);
            setIsDragging(false);
            setIsSettling(false);
            setDragX(0);
            setDragTo(null);
          }, 260);
          return;
        }

        setIsSettling(true);
        setDragX(0);
        window.setTimeout(() => {
          setIsDragging(false);
          setIsSettling(false);
          setDragTo(null);
        }, 220);
      }}
    >
      <div className={`w-full bg-white px-4 pt-3 pb-2 ${lg('lg:hidden')}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            {stepLabel && <div className="text-xs text-gray-500 leading-none">{stepLabel}</div>}
            {stepTitle && <div className="text-sm font-semibold text-gray-800 uppercase leading-tight">{stepTitle}</div>}
            {onChangeModel && (
              <button type="button" {...stopDrag} onClick={onChangeModel} className="mt-1 text-xs text-gray-500 underline underline-offset-2 hover:text-black">
                Change model
              </button>
            )}
          </div>
          <a href="https://www.touringdesignco.com/" rel="noopener noreferrer" target="_self" className="shrink-0">
            <img
              src="https://cdn.prod.website-files.com/68472be536d16d9004a489e5/6848a4a992a3670f3d9c446a_Logo%20touring.png"
              alt="Touring"
              className="h-6 w-auto invert"
            />
          </a>
        </div>
      </div>

      {(stepLabel || stepTitle) && (
        <div className={`absolute top-3 left-3 z-60 ${embedded ? 'hidden' : 'hidden lg:block'}`}>
          <div className="text-xs text-gray-500 leading-none">{stepLabel}</div>
          <div className="text-sm font-semibold text-gray-800 uppercase leading-tight">{stepTitle}</div>
          {onChangeModel && (
            <button type="button" {...stopDrag} onClick={onChangeModel} className="mt-1 text-xs text-gray-500 underline underline-offset-2 hover:text-black">
              Change model
            </button>
          )}
        </div>
      )}

      <div className={`absolute top-3 right-3 z-60 ${embedded ? 'hidden' : 'hidden lg:block'}`}>
        <a href="https://www.touringdesignco.com/" rel="noopener noreferrer" target="_self">
          <div className=" px-3 py-2  ">
            <img
              src="https://cdn.prod.website-files.com/68472be536d16d9004a489e5/6848a4a992a3670f3d9c446a_Logo%20touring.png"
              alt="Touring"
              className="h-6 w-auto invert"
            />
          </div>
        </a>
      </div>

      {viewLoading && (
        <div className="absolute inset-0 bg-white flex items-center justify-center z-50 animate-fadeIn">
          <div className="text-center">
            <svg className="animate-spin h-12 w-12 text-blue-600 mx-auto mb-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p className="text-gray-600 text-sm font-medium">Loading view...</p>
          </div>
        </div>
      )}

      <div
        className="relative w-full flex-1 flex items-center justify-center overflow-hidden"
      >
        {isDragging ? (
          <>
            <div
              className="absolute inset-0"
              style={{
                transform: `translateX(${dragX}px)`,
                transition: isSettling ? 'transform 240ms ease-out' : 'none',
              }}
            >
              {renderLayers(allViewLayers[dragFromRef.current] || [], dragFromRef.current)}
            </div>
            {dragTo && (
              <div
                className="absolute inset-0"
                style={{
                  transform: `translateX(${dragX + dragDir * (containerWidthRef.current || 1)}px)`,
                  transition: isSettling ? 'transform 240ms ease-out' : 'none',
                }}
              >
                {renderLayers(allViewLayers[dragTo] || [], dragTo)}
              </div>
            )}
          </>
        ) : transition ? (
          <>
            <div
              className="absolute inset-0"
              style={{
                transform:
                  transition.stage === 'end'
                    ? `translateX(${-transition.dir * 100}%)`
                    : 'translateX(0%)',
                transition: 'transform 300ms ease-out',
              }}
            >
              {renderLayers(allViewLayers[transition.from] || [], transition.from)}
            </div>
            <div
              className="absolute inset-0"
              style={{
                transform:
                  transition.stage === 'end'
                    ? 'translateX(0%)'
                    : `translateX(${transition.dir * 100}%)`,
                transition: 'transform 300ms ease-out',
              }}
            >
              {renderLayers(allViewLayers[transition.to] || [], transition.to)}
            </div>
          </>
        ) : activeLayers.length > 0 ? (
          <div className="absolute inset-0">
            {renderLayers(activeLayers, selectedView)}
          </div>
        ) : (
          <div className="text-center text-gray-400">
            <p className="text-xl mb-2">No preview available</p>
            <p className="text-sm">This option doesn't have images</p>
          </div>
        )}
      </div>

      {/* Controls sit in their own white strip below the picture (never over it). */}
      {/* Desktop: overlay toggles left, scenery picker right. */}
      {(viewOverlays.length > 0 || showSceneryPicker) && (
        <div {...stopDrag} className={`w-full shrink-0 bg-white px-4 py-4 items-center justify-between gap-6 ${embedded ? 'hidden' : 'hidden lg:flex'}`}>
          <div className="flex items-center gap-8">{overlayButtons('text-sm')}</div>
          {showSceneryPicker && <div className="flex items-center gap-2">{sceneryButtons('h-7 w-7')}</div>}
        </div>
      )}

      {/* Mobile: dots left, toggles + sceneries right (centered dots when there are none). */}
      <div {...stopDrag} className={`w-full shrink-0 bg-white px-4 py-2.5 flex items-center gap-3 ${viewOverlays.length || showSceneryPicker ? 'justify-between' : 'justify-center'} ${lg('lg:hidden')}`}>
        <div className="flex items-center gap-x-0.5">
          {viewKeys.map((vk) => (
            <button
              key={vk}
              className={`h-1.5 w-1.5 rounded-full ${vk === selectedView ? 'bg-black' : 'bg-gray-300'}`}
              onClick={() => {
                setSelectedView(vk);
                setViewLoading(false);
              }}
              aria-label={`View ${vk}`}
            />
          ))}
        </div>
        {(viewOverlays.length > 0 || showSceneryPicker) && (
          <div className="flex items-center gap-4 min-w-0">
            {overlayButtons('text-sm')}
            {showSceneryPicker && <div className="flex items-center gap-1.5">{sceneryButtons('h-5 w-5')}</div>}
          </div>
        )}
      </div>
    </div>
  );
};

export default LayeredPreview;
